import { ClientEvents } from 'common/events/ClientEvents';
import { NavClickElementName } from 'common/events/ClientTypes';
import React from 'react';
import { useShowLoginOrSignUpModal } from 'src/auth/modal/useShowLoginOrSignUpModal';
import { PromoCodeType, useGetNewUserPromoCodeQuery } from 'src/gqlReactTypings.generated.d';
import { usePromotionalsHook } from 'src/promotionals/usePromotionalsHook';
import { ButtonColors, ButtonSizes } from 'src/shared/design-system/Buttons';
import { useTracker } from 'src/shared/hooks/useTracker';
import styled from 'styled-components';
import { match, P } from 'ts-pattern';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { Colors, Font, Spacing } from 'web-common/src/shared/styles';
import { SignUpButton } from '../styles';

export const LoginButton = styled.button`
  position: relative;
  background: none;
  padding: ${Spacing.NONE};
  height: 100%;

  border: none;
  color: ${Colors.GRAY_DARK_20};
  font-weight: ${Font.Weight.BOLD};
  font-size: ${Font.Size.XXS};
  font-family: ${Font.Family.BODY_SANS};

  &:hover {
    color: ${Colors.GRAY_DARK_20};
    text-decoration: none;

    // Show a gray underline box on hover
    &:after {
      content: '';
      position: absolute;
      height: 3px;
      left: 0;
      bottom: 0;
      width: 100%;

      background-color: ${Colors.GRAY_LITE_20};
    }
  }
`;

interface AccountSettingsLinksProps {
  signupColor?: ButtonColors;
}

export const AccountSettingsLinks: React.FC<AccountSettingsLinksProps> = ({ signupColor = ButtonColors.RED }) => {
  const { isSmallScreen } = useScreenContext();
  const tracker = useTracker();

  const { data } = useGetNewUserPromoCodeQuery();
  const { code } = usePromotionalsHook();
  const { show: showLoginModal } = useShowLoginOrSignUpModal();

  const handleClick = (eventElementName: NavClickElementName) => () => {
    const promoCode = match(code)
      .with({ promoCodeType: PromoCodeType.Referral }, { isShefToConsumerPromo: true }, () => code)
      .with({ code: P.not(P.nullish) }, (code) => code)
      .otherwise(() => data?.newUserPromoCode);
    showLoginModal({ signUpPromoCode: promoCode });
    tracker.track(ClientEvents.NAV_CLICK, { elementName: eventElementName });
  };

  return (
    <>
      {!isSmallScreen && (
        <LoginButton onClick={handleClick('log-in')} data-cy='login-nav-item-link'>
          Log in
        </LoginButton>
      )}
      <SignUpButton
        color={signupColor}
        onClick={handleClick('register')}
        size={ButtonSizes.S}
        data-cy='sign-up-nav-item-link'>
        Sign up
      </SignUpButton>
    </>
  );
};
